// In this file, all Page components from 'src/pages` are auto-imported. Nested
// directories are supported, and should be uppercase. Each subdirectory will be
// prepended onto the component name.
//
// Examples:
//
// 'src/pages/HomePage/HomePage.js'         -> HomePage
// 'src/pages/Admin/BooksPage/BooksPage.js' -> AdminBooksPage

import { Router, Route, PrivateSet, Set } from '@cedarjs/router'

import AppLayout from 'src/layouts/AppLayout/AppLayout'
import AuthenticatorLayout from 'src/layouts/AuthenticatorLayout/AuthenticatorLayout'
import FormSubmissionLayout from 'src/layouts/FormSubmissionLayout/FormSubmissionLayout'
import AdminIntegrationsPage from './pages/AdminIntegrationsPage/IntegrationsPage'
import TaskLinkingPage from './pages/TaskLinkingPage/TaskLinkingPage'
import MonitoringDashboardPage from './pages/MonitoringDashboardPage/MonitoringDashboardPage'

import { useAuth } from './auth'
import WorkOrderDetailPage from './pages/WorkOrderDetailPage/WorkOrderDetailPage'
import DueListPageV2 from './pages/DueListPageV2Page/DueListPageV2'
import MaintenanceItemsPageV2 from './pages/MaintenanceItemsPageV2Page/MaintenanceItemsPageV2'
import PurchaseOrderDetailPageV2 from './pages/PurchaseOrderDetailPage/PurchaseOrderDetailPageV2'
import AddAlternateProductsPage from './pages/AddAlternateProductsPage/AddAlternateProductsPage'
import SubmitFormPage from './pages/SubmitFormPage/SubmitFormPage'
import SubmitFormConfirmationPage from './pages/SubmitFormConfirmationPage/SubmitFormConfirmationPage'
import ViewFormSubmissionPage from './pages/ViewFormSubmissionPage/ViewFormSubmissionPage'
import FormTemplatePage from './pages/FormTemplatePage/FormTemplatePage'
import ExternalWorkOrderDetailPage from './pages/ExternalWorkOrderDetailPage/ExternalWorkOrderDetailPage'

const Routes = () => {
  return (
    <Router useAuth={useAuth}>
      <Route path="/sign-up" page={SignUpPage} name="signUp" />
      <Route path="/self-onboarding" page={SelfOnboardingPage} name="selfOnboarding" />
      <Route path="/forbidden" page={ForbiddenPage} name="forbidden" />
      <Route path="/" page={LandingPage} name="landing" />
      <Route path="/login" page={LoginPage} name="login" />

      {/* Public form submission routes - no authentication required */}
      <Set wrap={[FormSubmissionLayout]}>
        <Route path="/forms/submit/{id}" page={SubmitFormPage} name="submitForm" />
        <Route path="/forms/submit/{id}/confirmation" page={SubmitFormConfirmationPage} name="submitFormConfirmation" />
      </Set>

      <PrivateSet wrap={[AppLayout]} unauthenticated="forbidden" roles="ww-user">
        <Route path="/admin" redirect="/admin/users" />
        <Route path="/admin/" redirect="/admin/users" />
        <Route path="/admin/references" page={AdminReferencesPage} name="adminReferences" />
        <Route path="/admin/support" page={AdminSupportPage} name="adminSupport" />
        <Route path="/admin/aircrafts" page={AdminAircraftsPage} name="adminAircrafts" />
        <Route path="/admin/aircraft-templates" page={AdminAircraftTemplatesPage} name="adminAircraftTemplates" />
        <Route path="/admin/self-onboarding" page={AdminSelfOnboardingPage} name="adminSelfOnboarding" />
        <Route path="/admin/component-templates/{componentTemplateId}" page={AdminComponentTemplatesPage} name="adminComponentTemplates" />
        <Route path="/admin/feature-settings" page={FeatureSettingsPage} name="featureSettings" />
        <Route path="/admin/onboarding" page={AdminOnboardingPage} name="adminOnboarding" />
        <Route path="/admin/integrations" page={AdminIntegrationsPage} name="adminIntegrations" />
        <Route path="/admin/task-linking" page={TaskLinkingPage} name="adminTaskLinking" />
        <Route path="/admin/monitoring-dashboard" page={MonitoringDashboardPage} name="monitoringDashboard" />
        <Route path="/admin/organizations" page={AdminOrganizationsPage} name="adminOrganizations" />
        <Route path="/admin/organizations/{id}" page={AdminOrganizationDetailPage} name="adminOrganizationDetail" />
        <Route path="/admin/{component}" page={AdministrationPage} name="administration" />
        <Route path="/admin/{component}/{componentId}" page={AdministrationPage} name="administrationId" />
      </PrivateSet>
      {
        //AuthenticatorLayout is required to set the active org
      }
      <PrivateSet wrap={[AuthenticatorLayout, AppLayout]} unauthenticated="login">
        {/* dashboards */}
        <Route path="/{orgName}" redirect="fleetDashboard" name="orgHomePage" />
        <Route path="/{orgName}/fleet-dashboard" page={FleetDashboardV2Page} name="fleetDashboard" />
        <Route path="/{orgName}/aircraft/{id}" page={PlaneDashboardPage} name="planeDashboard" />

        {/* due list and maintenance items */}
        <Route path="/{orgName}/due-list" page={DueListPageV2} name="dueList" />
        <Route path="/{orgName}/maintenance-items" page={MaintenanceItemsPageV2} name="maintenanceItems" />
        <Route path="/{orgName}/maintenance/{id}" page={MaintenanceItemPageV2Page} name="maintenanceItem" />
        <Route path="/{orgName}/create-task" page={CreateTaskPage} name="createTask" />

        <Route path="/{orgName}/onboarding/aircraft/{id}" page={AircraftOnboardingPage} name="aircraftOnboarding" />

        {/* bulk compliances */}
        <Route path="/{orgName}/compliance/{ledgerId}" page={BulkComplianceDetailPage} name="bulkCompliance" />
        <Route path="/{orgName}/compliance/{ledgerId}/sign" page={BulkComplianceSignPage} name="bulkComplianceSign" />

        {/* logbook entries */}
        <Route path="/{orgName}/logbook-entries" page={LogbookEntriesLandingPage} name="logbookEntriesLanding" />
        <Route path="/{orgName}/ledger-logbook-entry/{ledgerId}" page={LogbookEntryPage} name="ledgerLogbookEntry" />
        <Route path="/{orgName}/logbook-entry/{entryId}" page={LogbookEntryPage} name="logbookEntry" />

        {/* logbook ingestion */}
        <Route path="/{orgName}/logbook-ingestion/{id}" page={LogbookIngestionPage} name="logbookIngestion" />
        <Route path="/{orgName}/logbook-ingestions" page={LogbookIngestionsPage} name="logbookIngestions" />

        {/* complete compliances */}
        <Route path="/{orgName}/work-completed" page={PastCompliancePage} name="workCompleted" />

        {/* work orders */}
        <Route path="/{orgName}/work-order" redirect="/{orgName}/work-orders/scheduled" />
        <Route path="/{orgName}/work-order/{id}" page={WorkOrderDetailPage} name="workOrder" />
        <Route path="/{orgName}/work-orders/{ordersDiscriminator}" page={WorkOrdersPage} name="workOrders" />

        {/* external work orders */}
        <Route path="/{orgName}/external-work-orders" page={ExternalWorkOrdersPage} name="externalWorkOrders" />
        <Route path="/{orgName}/external-work-orders/{section}" page={ExternalWorkOrdersPage} name="externalWorkOrdersSection" />
        <Route path="/{orgName}/external-work-order/create" page={CreateExternalWorkOrderWizardPage} name="createExternalWorkOrder" />
        <Route path="/{orgName}/external-customer/new" page={NewExternalCustomerPage} name="newExternalCustomer" />
        <Route path="/{orgName}/external-work-order/{id}" page={ExternalWorkOrderDetailPage} name="externalWorkOrderDetail" />

        <Route path="/{orgName}/invoice/{id}" page={InvoicePage} name="invoice" />

        <Route path="/{orgName}/activity-log" page={ActivityLogPage} name="activityLog" />

        {/* purchase orders */}
        <Route path="/{orgName}/purchase-orders" page={PurchaseOrdersPageV2Page} name="purchaseOrders" />
        <Route path="/{orgName}/purchase-orders/requested-items" page={PurchaseOrderRequestedItemsPage} name="purchaseOrderRequestedItems" />
        <Route path="/{orgName}/purchase-order/{id}" page={PurchaseOrderDetailPageV2} name="purchaseOrder" />
        <Route path="/{orgName}/purchase-order/{id}/ingest" page={PurchaseOrderIngestPage} name="purchaseOrderIngest" />

        {/* document hub */}
        <Route path="/{orgName}/document-hub" page={DocumentHubPage} name="documentHub" />
        <Route path="/{orgName}/document-hub/{aircraftId}" page={DocumentHubPage} name="documentHubByAircraft" />

        <Route path="/{orgName}/reports" page={ReportsPage} name="reports" />

        <Route path="/{orgName}/inbox" page={InboxPage} name="inbox" />

        <Route path="/{orgName}/settings" page={SettingsPage} name="settings" />

        <Route path="/{orgName}/feature-not-enabled" page={FeatureNotEnabledPage} name="featureNotEnabled" />

        {/* inventory */}
        <Route path="/{orgName}/products" page={ProductsListPage} name="productsList" />
        <Route path="/{orgName}/due-products" page={DueProductsPage} name="dueProducts" />
        <Route path="/{orgName}/products/{id}" page={ProductDetailPage} name="productDetail" />
        <Route path="/{orgName}/item/{id}" page={InventoryItemDetailPage} name="inventoryItemDetail" />
        <Route path="/{orgName}/tools" page={ToolsListPage} name="toolsList" />
        <Route path="/{orgName}/vendors" page={VendorsListPage} name="vendorsList" />
        <Route path="/{orgName}/add-alternate-products" page={AddAlternateProductsPage} name="addAlternateProducts" />

        {/* Components */}
        {/* adopt github style "plural list page, singular detail page naming scheme" */}
        {/* use redirects so users can be nudged into the right place if they mix it up */}
        <Route path="/{orgName}/components" page={ComponentsListPage} name="componentsList" />
        <Route path="/{orgName}/component" redirect="/{orgName}/components" />
        <Route path="/{orgName}/component/{id}" page={ComponentsDetailPage} name="componentDetail" />
        <Route path="/{orgName}/components/{id}" redirect="/{orgName}/component/{id}" />

        {/* forms - authenticated routes */}
        <Route path="/{orgName}/forms" page={FormsLandingPage} name="forms" />
        <Route path="/{orgName}/forms/create" page={CreateFormPage} name="createForm" />
        <Route path="/{orgName}/forms/{id}" page={FormTemplatePage} name="formTemplate" />
        <Route path="/{orgName}/forms/submissions/view/{id}" page={ViewFormSubmissionPage} name="viewFormSubmission" />

        {/**
         *  TODO: in the fullness of time, lets rip out the v1 routes, their page names include:
         *
         *  MaintenanceItemPage
         *  WorkCompletedPage
         *  WorkOrderPage
         *  PurchaseOrdersPage
         *  PurchaseOrderDetailPage
         */}

        {/**
         *  TIP: These routes are relic redirects that point from the old v2 routes to the defaulted routes that are the successors to v2 routing
         *  TODO: in the fullness of time, lets rip out these v2 redirect routes, their page names include:
         */}
        <Route path="/{orgName}/v2" redirect="/{orgName}/fleet-dashboard" />
        <Route path="/{orgName}/v2/fleet-dashboard" redirect="/{orgName}/fleet-dashboard" />
        <Route path="/{orgName}/v2/maintenance-item-page/{id}" redirect="/{orgName}/maintenance/{id}" />
        <Route path="/{orgName}/past-compliance" redirect="/{orgName}/work-completed" />
        <Route path="/{orgName}/compliance/v2/{ledgerId}" redirect="/{orgName}/compliance/{ledgerId}" />
        <Route path="/{orgName}/work-order/v2/{id}" redirect="/{orgName}/work-order/{id}" />
        <Route path="/{orgName}/due-list/v2" redirect="/{orgName}/due-list" />
        <Route path="/{orgName}/maintenance-items/v2" redirect="/{orgName}/maintenance-items" />
        <Route path="/{orgName}/purchase-orders/v2" redirect="/{orgName}/purchase-orders" />
        <Route path="/{orgName}/purchase-order/v2/{id}" redirect="/{orgName}/purchase-order/{id}" />
      </PrivateSet>
      <Route notfound page={NotFoundPage} />
    </Router>
  )
}

export default Routes
